import { borcAlacak, kucukTr, metinOku, sayiOku, sayiOkuBos, yuvarla2, type BorcAlacak } from '../rapor-ekrani/bicim';

/**
 * S334 Cari Hesap Listesi: ds satırlarının ekran karşılıkları. Kolon adları VERİ SÖZLEŞMESİDİR
 * (SQL ayrı yazılıyor); ds'ten gelen ham satır burada bir kez okunur, ekran ham alana dokunmaz.
 */

/** `liste` ds'i, parametre `@SONTARIH`. Bakiyesi sıfır olan cariler de gelir. */
export interface HamCariSatiri {
  cari_id: number | string;
  cari_kodu: string | null;
  cari: string | null;
  temsilci: string | null;
  vade: number | string | null;
  borc: number | string | null;
  alacak: number | string | null;
  bakiye: number | string | null;
  son_islem_tarihi: string | null;
  /** Carinin TÜM hareket sayısı (tarih kesmeden). Eski ds metninde yoksa gelmez. */
  hareket_sayisi?: number | string | null;
}

/**
 * S342: hareket dökümü bu sayının ÜSTÜNDEKİ caride açılmaz. Perakende (peşin satış) carisinin hareketi
 * yüz binlerdir (bir firmada 1.187.029): sorgu 19 sn sürer ve satırların hepsi tarayıcıya iner.
 */
export const HAREKET_ESIGI = 20000;

export interface CariSatiri {
  id: string;
  kod: string;
  ad: string;
  temsilci: string;
  /** Vade günü; kartta boşsa null. */
  vade: number | null;
  borc: number;
  alacak: number;
  bakiye: number;
  ba: BorcAlacak;
  sonIslem: string | null;
  /** Carinin tüm hareket sayısı; ds vermiyorsa null (o zaman eşik uygulanmaz). */
  hareketSayisi: number | null;
  /** Arama anahtarı (kod + ad, Türkçe küçük harf): on binlerce satırda tuş başına yeniden katlanmasın. */
  arama: string;
}

export function cariSatiri(h: HamCariSatiri): CariSatiri {
  const kod = metinOku(h.cari_kodu);
  const ad = metinOku(h.cari);
  const bakiye = sayiOku(h.bakiye);
  return {
    id: String(h.cari_id),
    kod,
    ad,
    temsilci: metinOku(h.temsilci),
    vade: sayiOkuBos(h.vade),
    borc: sayiOku(h.borc),
    alacak: sayiOku(h.alacak),
    bakiye,
    ba: borcAlacak(bakiye),
    sonIslem: h.son_islem_tarihi || null,
    hareketSayisi: sayiOkuBos(h.hareket_sayisi),
    arama: kucukTr(`${kod} ${ad}`),
  };
}

/** Hareket dökümü açılamayacak kadar çok hareketli cari mi (eşik DAHİL değil). */
export function hareketiCokMu(c: Pick<CariSatiri, 'hareketSayisi'>): boolean {
  return c.hareketSayisi !== null && c.hareketSayisi > HAREKET_ESIGI;
}

export type BakiyeTipi = 'tumu' | 'borclu' | 'alacakli' | 'bakiyesiz';

export const BAKIYE_SECENEKLERI: ReadonlyArray<{ key: BakiyeTipi; label: string }> = [
  { key: 'tumu', label: 'Tümü' },
  { key: 'borclu', label: 'Borçlular' },
  { key: 'alacakli', label: 'Alacaklılar' },
  { key: 'bakiyesiz', label: 'Bakiyesiz' },
];

/**
 * İstemci süzgeci. Borçlu / alacaklı / bakiyesiz ayrımı satırdaki `ba` işaretinden, yani ekrana
 * yazılan YUVARLANMIŞ bakiyeden: |bakiye| < 0,005 bakiyesizdir, "₺0,00" yazan cari borçlularda çıkmaz.
 */
export function carileriSuz(satirlar: readonly CariSatiri[], tip: BakiyeTipi, ara: string): CariSatiri[] {
  const q = kucukTr(ara.trim());
  const isaret = tip === 'borclu' ? 'B' : tip === 'alacakli' ? 'A' : tip === 'bakiyesiz' ? '-' : null;
  return satirlar.filter((c) => (isaret === null || c.ba === isaret) && (q === '' || c.arama.includes(q)));
}

/** `hareket` ds'i, parametreler `@CARI`, `@BASTARIH`, `@BITTARIH`. `sira` 0 = "Devreden" satırı. */
export interface HamHareketSatiri {
  sira: number | string;
  tarih: string | null;
  islem: string | null;
  belge_no: string | null;
  aciklama: string | null;
  vade: string | null;
  borc: number | string | null;
  alacak: number | string | null;
  yuruyen_bakiye: number | string | null;
}

export interface HareketSatiri {
  sira: number;
  tarih: string | null;
  islem: string;
  belgeNo: string;
  aciklama: string;
  vade: string | null;
  borc: number;
  alacak: number;
  yuruyen: number;
}

export interface CariHareketOzeti {
  /** Dönem başından önceki bakiye (`sira` 0 satırının yürüyen bakiyesi; satır yoksa 0). */
  devreden: number;
  hareketler: HareketSatiri[];
  donemBorc: number;
  donemAlacak: number;
  /** Son hareketin yürüyen bakiyesi; hareket yoksa devreden. */
  bakiye: number;
}

export function hareketOzeti(ham: readonly HamHareketSatiri[]): CariHareketOzeti {
  let devreden = 0;
  const hareketler: HareketSatiri[] = [];
  for (const h of ham) {
    const sira = sayiOku(h.sira);
    if (sira === 0) {
      devreden = sayiOku(h.yuruyen_bakiye);
      continue;
    }
    hareketler.push({
      sira,
      tarih: h.tarih || null,
      islem: metinOku(h.islem),
      belgeNo: metinOku(h.belge_no),
      aciklama: metinOku(h.aciklama),
      vade: h.vade || null,
      borc: sayiOku(h.borc),
      alacak: sayiOku(h.alacak),
      yuruyen: sayiOku(h.yuruyen_bakiye),
    });
  }
  hareketler.sort((a, b) => a.sira - b.sira);
  let donemBorc = 0, donemAlacak = 0;
  for (const h of hareketler) {
    donemBorc += h.borc;
    donemAlacak += h.alacak;
  }
  return {
    devreden,
    hareketler,
    donemBorc: yuvarla2(donemBorc),
    donemAlacak: yuvarla2(donemAlacak),
    bakiye: hareketler.length > 0 ? hareketler[hareketler.length - 1].yuruyen : devreden,
  };
}
